import _ from 'lodash';
import FormElement from './FormElement';
import TextInputMixin from './mixins/TextInputMixin';
import ReadOnlyMixin from './mixins/ReadOnlyMixin';
import mixin from 'privates/utils/mixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as members from 'privates/privateMembers';
import {validate, validateMinimum, validateMaximum} from 'privates/validations/validation';
import {runValidationsAndSyncAfterChange} from 'privates/validationsService/validationsService';
import {nullIfNil} from 'privates/utils';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import RequiredMixin from './mixins/RequiredMixin';

/**
 * @class TextInput
 * @summary An input element for capturing small amounts of text.
 * @classdesc
 *  A text input is configured to receive
 *  <a href="https://www.wix.com/support/html5/article/text-input-settings" target="_blank">different types
 *  of information</a>, such as strings, numbers, or passwords.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.TextInputMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ReadOnlyMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.inputs.TextInput
 * @definitionId wysiwyg.viewer.components.inputs.TextInput
 * @tagname textinput
 */
class TextInput extends FormElement {

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets a text input's value.
     * @description
     *  To reset the text input, restoring any placeholder text, set the value
     *  property to `null` or `undefined`.
     *
     * @note
     *  Changing a text input's `value` in code does not trigger an `onChange` event.
     *
     *  Using the **Input Settings** panel in the Editor you can set the type of
     *  a text input. That type is used only for validation purposes. The value
     *  returned by the `value` property of a text input is always a string,
     *  regardless of the type set in the Editor.
     *
     *  If an element is connected to a dataset, setting the element's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     *
     *  To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {external:String}
     * @memberof $w.TextInput
     * @override
     */

  /**
   * @member value
   * @memberof $w.TextInput
   * @param {external:String} value
   */


    /**
     * @member max
     * @label property
     * @syntax
     * get max(): Number
     * set max(value: Number): void
     * @summary Sets or gets the maximum value for a text input element whose
     *  type is set to **Number**.
     * @description
     *  Setting the `max` property restricts users from changing
     *  the text input's value to a number greater than `max` if the
     *  text input is set to be a **Number** type. However, it does not limit
     *  your ability to change the value to a number greater than `max`
     *  programmatically.
     *
     *  Set `max` to `undefined` to remove the current maximum value limitation.
     *
     *  Getting the `max` property returns the maximum value a user
     *  can enter into the text input.
     *
     *  You can also set an element's `max` property in the Editor using the Settings pane:
     *  ![Input Settings pane](images/input_settings-max_value.png)
     * @snippet [TextInput-max_get.es6=Gets a text input's max value]
     * @snippet [TextInput-max_set.es6=Sets a text input's max value]
     * @type {external:Number}
     * @memberof $w.TextInput
     * @see [min](#min)
     * @instance
     */
    get max() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);

        return compData && compData.max;
    }

  /**
   * @member max
   * @memberof $w.TextInput
   * @param {external:Number} max
   */
    set max(max) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const minValue = this.min;

        if(!validate({
            compType,
            functionName: 'max',
            dataToValidate: {max},
            compName: this.id
        }) ||
            !_.isNil(max) && _.isNumber(minValue) && !validateMinimum({
                functionName: 'max',
                propertyName: 'max',
                value: max,
                minValue
            })){
            return;
        }

        RMI.setData(compId, {max: nullIfNil(max)});
        runValidationsAndSyncAfterChange(RMI, compId, this.value);
    }

    /**
     * @member min
     * @label property
     * @syntax
     * get min(): Number
     * set min(value: Number): void
     * @summary Sets or gets the minimum value for a text input element whose
     *  type is set to **Number**.
     * @description
     *  Setting the `min` property restricts users from changing
     *  the text input's value to a number lower than `min` if the
     *  text input is set to be a **Number** type. However, it does not limit
     *  your ability to change the value to a number lower than `min`
     *  programmatically.
     *
     *  Set `min` to `undefined` to remove the current maximum value limitation.
     *
     *  Getting the `min` property returns the minimum value a user
     *  can enter into the text input.
     *
     *  You can also set an element's `min` property in the Editor using the Settings pane:
     *  ![Input Settings pane](images/input_settings-min_value.png)
     * @snippet [TextInput-min_get.es6=Gets a text input's min value]
     * @snippet [TextInput-min_set.es6=Sets a text input's min value]
     * @type {external:Number}
     * @memberof $w.TextInput
     * @see [max](#max)
     * @instance
     */
    get min() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        return compData && compData.min;
    }

  /**
   * @member min
   * @memberof $w.TextInput
   * @param {external:Number} min
   */
    set min(min) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const maxValue = this.max;

        if(!validate({
            compType,
            functionName: 'min',
            dataToValidate: {min},
            compName: this.id
        }) ||
            !_.isNil(min) && _.isNumber(maxValue) && !validateMaximum({
                functionName: 'min',
                propertyName: 'min',
                value: min,
                maxValue
            })){
            return;
        }

        RMI.setData(compId, {min: nullIfNil(min)});
        runValidationsAndSyncAfterChange(RMI, compId, this.value);
    }

    /**
     * @member inputType
     * @label property
     * @syntax
     * get inputType(): InputType
     * set inputType(value: InputType): void
     * type InputType = "text" | "password" | "number" | "url" | "tel"
     * @summary Sets or gets the input type of an Input element.
     * @description
     *  Setting the `inputType` property sets what type of data a
     *  user can enter into the text input element. However, it does not limit
     *  your ability to change the value programmatically.
     *
     *  Getting the `inputType` property gets what type of data a
     *  user can enter into the text input element.
     *
     *  The text input types are:
     *
     *  + `"text"`: Can contain any type of character.
     *  + `"password"`: A password field. Text is hidden with bullets (•••).
     *  + `"number"`: A valid number.
     *  + `"email"`: A valid email address.
     *  + `"url"`: A valid web address.
     *  + `"tel"`: Can contain only digits and certain symbols.
     *
     *
     *  You can also set an element's `inputType` property in the Editor using the Settings pane:
     *  ![Input Settings pane](images/input_settings-type.png)
     * @snippet [TextInput-inputType_get.es6=Gets a text input's input type]
     * @snippet [TextInput-inputType_set.es6=Sets a text input's input type]
     * @type {external:String}
     * @memberof $w.TextInput
     * @attr {external:String}
     */
    get inputType() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);

        return compData && compData.textType;
    }

  /**
   * @member inputType
   * @memberof $w.TextInput
   * @param {external:String} newType
   */
    set inputType(newType) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'inputType',
            dataToValidate: {inputType: newType},
            compName: this.id
        })) {
            return;
        }
        RMI.setData(compId, {
            textType: newType
        });
        runValidationsAndSyncAfterChange(RMI, compId, this.value);
    }

    toJSON() {
        const {id, type, global, rendered, readOnly, required, value, max, min, inputType} = this;
        return {id, type, global, rendered, readOnly, required, value, max, min, inputType};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the text input's styles.
     * @description
     *  The following styles can be used with text input elements:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a text input's styles, gets or sets the styles of the
     *  text input's regular state. It does not set the styles of the text input's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.TextInput
     * @readonly
     * @override
     */
}

mixin(TextInput.prototype, HiddenCollapsedMixin.prototype);
mixin(TextInput.prototype, DisabledMixin.prototype);
mixin(TextInput.prototype, TextInputMixin.prototype);
mixin(TextInput.prototype, FocusMixin.prototype);
mixin(TextInput.prototype, ReadOnlyMixin.prototype);
mixin(TextInput.prototype, ClickableMixin.prototype);
mixin(TextInput.prototype, StyleMixin.prototype);
mixin(TextInput.prototype, RequiredMixin.prototype);

export default TextInput;



// WEBPACK FOOTER //
// ./js/modules/ui/TextInput.es6